import type { executeQueryResponse } from '../../gen/react-query/platform'
import type { BatchQueryRequest, QueryRequest } from '../../gen/types/platform'

import {
  batchQuery,
  executeQuery,
  exportQuery,
} from '../../gen/react-query/platform'

/**
 * Options for {@link executeSormQuery}. Extends the generated `executeQuery`
 * request init (`RequestInit`) with a SORM-specific `queryName`; every other
 * field is forwarded to `executeQuery` as-is, so callers can add headers, pass
 * an abort `signal`, etc.
 */
interface ExecuteSormQueryOptions extends RequestInit {
  /**
   * snake_case identifier of the feature issuing the request (e.g.
   * `order_summary`), forwarded as the `?qn=` query param. SORM queries all
   * hit `POST /auth/v1/sorm/query`, so they're indistinguishable in network
   * devtools, traces, and logs; this label tells otherwise-identical POSTs
   * apart. Observability-only — it never affects query semantics or results.
   *
   * Required: an unlabelled query is invisible in traces, and the generated
   * transport makes it too easy to omit. Draw labels from a closed set fixed by
   * code, never from user data — each distinct value becomes a persistent
   * Datadog metric tag.
   */
  queryName: string
}

/**
 * Executes a SORM query against `POST /auth/v1/sorm/query`.
 *
 * Thin wrapper over the orval-generated `executeQuery` that maps `queryName`
 * to the endpoint's native `qn` observability query param and forwards any
 * remaining `RequestInit` fields (headers, `signal`, …) through unchanged.
 *
 * @param request - The SORM query request payload.
 * @param options - `queryName` plus any `RequestInit` overrides.
 * @returns The query response, matching the generated `executeQuery` shape.
 */
export function executeSormQuery(
  request: QueryRequest,
  options: ExecuteSormQueryOptions,
): Promise<executeQueryResponse> {
  const { queryName, ...fetchOverrides } = options
  return executeQuery(request, { qn: queryName }, fetchOverrides)
}

/**
 * Options for {@link executeSormBatchQuery}. Mirrors {@link ExecuteSormQueryOptions}:
 * a SORM-specific `queryName` plus any `RequestInit` overrides forwarded to the
 * generated `batchQuery` unchanged.
 */
interface ExecuteSormBatchQueryOptions extends RequestInit {
  /**
   * Observability label forwarded as `?qn=` — for a batch, the shared member
   * query name, or a bounded `mixed_batch_<n>` label when members' names differ
   * (each distinct label becomes a persistent Datadog metric tag value, so the
   * label set must stay small). See {@link ExecuteSormQueryOptions.queryName}.
   *
   * Optional, unlike the single-query path: a batch's label is derived from its
   * members rather than chosen by a caller, and there is no honest label for a
   * batch whose members are all unnamed.
   */
  queryName?: string
}

/**
 * Executes a batch of SORM queries against `POST /auth/v1/sorm/batch-query` and
 * returns the raw NDJSON `Response` for the caller to read and demultiplex.
 *
 * Thin transport wrapper over the orval-generated `batchQuery`, mapping
 * `queryName` to the endpoint's `qn` param and forwarding any `RequestInit`
 * overrides. Orval types the NDJSON response as `{ stream: TypedResponse<…> }`
 * (its streaming contract) and `customFetch` hands back the raw `Response`; we
 * return that stream untouched so the caller can consume it incrementally
 * (`ReadableStream`, one JSON line per query keyed by the request's client ids).
 * Parsing lives in the consumer (`@moment/sorm-client-data-access`), which owns
 * the per-line shape and runs on a lib with async-iteration support.
 *
 * @param request - The batch request: SORM queries each tagged with a client id.
 * @param options - `queryName` plus any `RequestInit` overrides.
 * @returns The NDJSON `Response` whose body streams one JSON line per query.
 */
export async function executeSormBatchQuery(
  request: BatchQueryRequest,
  options?: ExecuteSormBatchQueryOptions,
): Promise<Response> {
  const { queryName, ...fetchOverrides } = options ?? {}
  const response = await batchQuery(request, { qn: queryName }, fetchOverrides)
  return response.stream
}

/**
 * Options for {@link exportSormQuery}. Mirrors {@link ExecuteSormQueryOptions}:
 * a SORM-specific `queryName` plus any `RequestInit` overrides (headers, an
 * abort `signal`, …) forwarded to the generated `exportQuery` unchanged.
 */
interface ExportSormQueryOptions extends RequestInit {
  /** See {@link ExecuteSormQueryOptions.queryName}. */
  queryName: string
}

/**
 * Streams a SORM query's full result set as CSV from `POST /auth/v1/sorm/export`.
 *
 * Unlike {@link executeSormQuery}, the export endpoint ignores `limit`/`offset`
 * and streams every matching row (server-side, up to its own row cap), so this
 * is the path for "export everything I'm looking at" rather than one page. The
 * resolved value is the raw CSV text; non-2xx responses reject with a
 * `HorizonError` whose `message` carries the server's human-readable reason
 * (e.g. the "narrow the query with filters" guidance for oversize exports).
 *
 * @param request - The SORM query request payload (same shape as a `/query`).
 * @param options - `queryName` plus any `RequestInit` overrides.
 * @returns The CSV body as a string.
 */
export async function exportSormQuery(
  request: QueryRequest,
  options: ExportSormQueryOptions,
): Promise<string> {
  const { queryName, ...fetchOverrides } = options
  const response = await exportQuery(request, { qn: queryName }, fetchOverrides)
  // Orval types the CSV body as `{ data: Blob }` and `customFetch` returns a
  // Blob for `text/csv`; read it as text.
  return response.data.text()
}
